import { isValid, parseISO } from "date-fns";
import { ChevronRight } from "lucide-react";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  looksLikeHtml,
  sanitizeRichTextHtml,
} from "@/shared/ui/RichTextOrPlain";

type MeetingMinuteRowProps = {
  title: string;
  meetingDate: string;
  summary: string | null;
  onOpen?: () => void;
};

function parseMeetingDate(raw: string): Date | null {
  if (!raw?.trim()) return null;
  const fromIso = parseISO(raw.trim());
  if (isValid(fromIso)) return fromIso;
  const fallback = new Date(raw);
  return isValid(fallback) ? fallback : null;
}

function MinuteSummaryPreview({ summary }: { summary: string | null }) {
  if (!summary?.trim()) {
    return <p className="mt-1.5 text-muted-foreground text-sm">ບໍ່ມີສະຫຼຸບ</p>;
  }
  if (looksLikeHtml(summary)) {
    const safe = sanitizeRichTextHtml(summary);
    if (!safe.trim()) {
      return (
        <p className="mt-1.5 line-clamp-2 text-muted-foreground text-sm">
          {summary.replace(/<[^>]*>/g, "").trim() || "—"}
        </p>
      );
    }
    return (
      <div
        className="rich-text-inline mt-1.5 line-clamp-2 text-muted-foreground text-sm [&_ol]:my-0 [&_p:first-child]:mt-0 [&_p]:m-0 [&_ul]:my-0"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via sanitizeRichTextHtml
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    );
  }
  return (
    <p className="mt-1.5 line-clamp-2 whitespace-pre-line text-muted-foreground text-sm">
      {summary}
    </p>
  );
}

export function MeetingMinuteRow({
  title,
  meetingDate,
  summary,
  onOpen,
}: MeetingMinuteRowProps) {
  const d = parseMeetingDate(meetingDate);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`ເປີດບັນທຶກ: ${title}`}
      className="group flex w-full items-stretch gap-3 rounded-xl border border-border/80 bg-muted/20 p-3 text-left transition-colors hover:border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:gap-4 sm:p-4"
    >
      <div className="flex h-17 min-w-14 shrink-0 flex-col items-center justify-center rounded-lg border border-border/60 bg-background px-1.5 shadow-sm sm:h-18 sm:min-w-16">
        {d ? (
          <span className="text-center font-semibold text-xs tabular-nums leading-tight sm:text-sm">
            {formatDateLocal(d)}
          </span>
        ) : (
          <span className="px-1 text-center text-muted-foreground text-xs leading-tight">
            {meetingDate.length > 14
              ? `${meetingDate.slice(0, 12)}…`
              : meetingDate}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1 py-0.5">
        <h3 className="font-semibold text-base leading-snug tracking-tight">
          {title}
        </h3>
        <MinuteSummaryPreview summary={summary} />
      </div>

      <div className="flex shrink-0 items-center self-center">
        <ChevronRight
          className="size-5 text-muted-foreground transition duration-200 group-hover:translate-x-0.5 group-hover:text-foreground"
          aria-hidden
        />
      </div>
    </button>
  );
}
